import type {
	AppMenuConfig,
	MenuItem as MenuItemConfig,
	MenuSection as MenuSectionConfig,
} from '@atlassian/universal-create-common/bin-param/types';

import type { UniversalCreateMenuItem, UniversalCreateMenuSection } from '../../../../types/menu';
import { DEFAULT_OVERFLOW_THRESHOLD } from '../../../../types/menu-base';
import { isNativeMenuItem } from './itemTypeGuards';

/**
 * Converts a UniversalCreateMenuItem to MenuItemConfig format for the BFF API.
 */
export const convertItemToConfig = (item: UniversalCreateMenuItem): MenuItemConfig => ({
	type: isNativeMenuItem(item) ? 'native' : item.type,
	ati: item.ati.toString(),
	promoted: item?.promoted,
});

/**
 * Converts a UniversalCreateMenuSection to MenuSectionConfig format for the BFF API.
 */
export const convertSectionToConfig = (section: UniversalCreateMenuSection): MenuSectionConfig => {
	if (section.type === 'suggestionSection') {
		return {
			type: 'suggestionSection' as const,
			limit: section.limit ?? 5,
			...(section.excludeAtis && { excludeAtis: section.excludeAtis.map((ati) => ati.toString()) }),
			...(section.preferredAtis && {
				preferredAtis: section.preferredAtis.map((ati) => ati.toString()),
			}),
		};
	}

	if (section.type === 'appDefinedSection') {
		const overflowThreshold = Boolean(section.foldable)
			? typeof section.foldable === 'number'
				? section.foldable
				: DEFAULT_OVERFLOW_THRESHOLD
			: undefined;
		return {
			type: 'appDefinedSection' as const,
			items: section.items.map(convertItemToConfig),
			personalize: section.personalize,
			...(overflowThreshold && { overflowThreshold }),
		};
	}

	// Assert that we have handled all possible section types
	const exhaustive: never = section;
	return exhaustive;
};

/**
 * Converts sections configuration to AppMenuConfig format for the BFF API.
 */
export const sectionsToAppMenuConfig = (sections: UniversalCreateMenuSection[]): AppMenuConfig => ({
	appMenuConfig: sections.map(convertSectionToConfig),
});
